import { useEffect } from 'react';

type UsePollingOptions = {
	pollFn: () => void | Promise<void>;
	pollingIntervalMs?: number;
};

export function usePolling({ pollFn, pollingIntervalMs }: UsePollingOptions) {
	useEffect(() => {
		let pollingId: number | null = null;

		const isPageVisible = () =>
			typeof document === 'undefined' || document.visibilityState === 'visible';

		const stopPolling = () => {
			if (pollingId !== null) {
				window.clearInterval(pollingId);
				pollingId = null;
			}
		};

		const startPolling = () => {
			if (!pollingIntervalMs || pollingIntervalMs <= 0 || !isPageVisible()) {
				return;
			}

			stopPolling();
			pollingId = window.setInterval(() => {
				if (isPageVisible()) {
					pollFn();
				}
			}, pollingIntervalMs);
		};

		pollFn();
		startPolling();

		const onVisibilityChange = () => {
			if (isPageVisible()) {
				pollFn();
				startPolling();
				return;
			}

			stopPolling();
		};

		if (typeof document !== 'undefined' && pollingIntervalMs) {
			document.addEventListener('visibilitychange', onVisibilityChange);
		}

		return () => {
			stopPolling();

			if (typeof document !== 'undefined' && pollingIntervalMs) {
				document.removeEventListener('visibilitychange', onVisibilityChange);
			}
		};
	}, [pollFn, pollingIntervalMs]);
}

export default usePolling;
